import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { useGetExpensesCategory } from "../../hooks/useExpenceApi";
import { ExpenseCategoryDialog } from "../Models/ExpenseCategory";
import { useAuth } from "../../hooks/AuthContext";

const getTodayDate = () => new Date().toISOString().split("T")[0];

export const ExpensesForm = ({ onSubmit, isLoading }) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      expenses: [
        {
          date: getTodayDate(),
          employee: "",
          category: "",
          amount: "",
          storeName: "",
          description: "",
          status: "",
        },
      ],
    },
  });

  const { data: categoryData, isLoading: categoryLoading } =
    useGetExpensesCategory();
  const categoryExpense = categoryData?.expenses || [];

  const { fields, append, remove } = useFieldArray({ control, name: "expenses" });
  const [employees, setEmployees] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [assignToMultiple, setAssignToMultiple] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const { user } = useAuth();

  useEffect(() => {
    let isMounted = true;
    const fetchEmployees = async () => {
      try {
        const res = await api.get("/employees");
        if (isMounted) setEmployees(res.data.data.employees);
      } catch (err) {
        console.error(err);
      }
    };
    // Fetch employees only if user is NOT an employee
    if (user?.role !== "employee") fetchEmployees();

    return () => {
      isMounted = false;
    };
  }, [user?.role]);

  const handleFormSubmit = (data) => {
    const payload = {
      expenses: data.expenses.map((exp) => {
        const selectedEmployee =
          user.role === "employee"
            ? user.employeeProfile
            : assignToMultiple
              ? employees.find((e) => e._id === exp.employee)
              : employees.find((e) => e._id === selectedEmployeeId);
  
        const selectedCategory = categoryExpense.find((c) => c._id === exp.category);
  
        // ✅ FIX: Parse date in local timezone and add current time
        const [year, month, day] = exp.date.split('-');
        const dateWithCurrentTime = new Date(year, month - 1, day);
        const now = new Date();
        dateWithCurrentTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
        
        const epochSeconds = Math.floor(dateWithCurrentTime.getTime() / 1000); // ✅ Store in variable
  
        return {
          employeeId: selectedEmployee?._id || "",
          employeeName: selectedEmployee?.name || "",
          categoryId: selectedCategory?._id || "",
          categoryName: selectedCategory?.categoryName || "",
          description: exp.description,
          shopName: exp.storeName,
          status: "pending",
          amount: Number(exp.amount),
          date: epochSeconds, // ✅ Use the variable
          createdBy: user?.employeeProfile?._id,
        };
      }),
    };
  
    onSubmit(payload);
  
    reset({
      expenses: [
        {
          employee: "",
          amount: "",
          storeName: "",
          description: "",
          date: getTodayDate(),
          status: "",
          category: "",
        },
      ],
    });
    setSelectedEmployeeId("");
  };
  
  

  return (
    <>
      <div className=" pl-3 rounded">
        {user?.role !== "employee" && (
          <>
            <h1 className="text-xl font-bold mb-2">Create Expense</h1>
            <p className="text-gray-600 mb-3">
              Choose how you want to add expense to employees.
            </p>
          </>
        )}

        {/* HR/Admin Section */}
        {user?.role !== "employee" && (
          <>
            {/* Radio Buttons */}
            <div className="w-full flex mb-4 justify-between items-center ">
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="assignmentMode"
                    checked={assignToMultiple === false}
                    onChange={() => setAssignToMultiple(false)}
                    className="form-radio h-5 w-5 text-[#2C4E86]"
                  />
                  <span className="ml-2 text-gray-700">
                    Assign all expenses to one employee
                  </span>
                </label>

                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="assignmentMode"
                    checked={assignToMultiple === true}
                    onChange={() => setAssignToMultiple(true)}
                    className="form-radio h-5 w-5 text-[#2C4E86]"
                  />
                  <span className="ml-2 text-gray-700">
                    Assign each expense to different employees
                  </span>
                </label>
              </div>

              {/* Create Category Button */}
              <div>
                <button
                  onClick={() => setIsDialogOpen(true)}
                  className="px-2 py-2 text-sm flex gap-1 bg-[#2C4E86] text-white rounded hover:bg-[#1f3764]"
                >
                  <Plus size={19} /> Create Category
                </button>

                <ExpenseCategoryDialog
                  open={isDialogOpen}
                  onClose={() => setIsDialogOpen(false)}
                />
              </div>
            </div>

            {/* Single Employee Dropdown */}
            {assignToMultiple === false && (
              <div className="mb-6 max-w-sm">
                <label className="block text-gray-700 mb-2">Select Employee</label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employeeId})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Employee Section: show employee name instead of select */}
        {user?.role === "employee" && (
          <div className="flex items-center mb-3 gap-2 bg-white border border-gray-200 rounded px-3 py-2 shadow-sm w-fit">
            <label className="text-sm font-semibold text-gray-700">
              Employee Name:
            </label>
            <p className="text-sm text-gray-900 font-medium">
              {user.employeeProfile?.name || "N/A"}
            </p>
          </div>
        )}


        {/* Expense Form */}
        {assignToMultiple !== null || user?.role === "employee" ? (
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="bg-white rounded-lg shadow-sm border p-6 overflow-x-auto"
          >
            <div className="space-y-6">
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-4 items-start">
                  {/* Employee Field (only multiple assign mode for HR/Admin) */}
                  {assignToMultiple === true && user.role !== "employee" && (
                    <div className="w-[15%]">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Employee
                      </label>
                      <select
                        {...register(`expenses.${index}.employee`, {
                          required: "Employee is required",
                        })}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select Employee</option>
                        {employees.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {emp.name} ({emp.employeeId})
                          </option>
                        ))}
                      </select>
                      {errors?.expenses?.[index]?.employee && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.expenses[index].employee.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Category */}
                  <div className="w-[15%]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      {...register(`expenses.${index}.category`, {
                        required: "Category is required",
                      })}
                      className="w-full p-2 border rounded"
                      defaultValue=""
                      disabled={categoryLoading}
                    >
                      <option value="">Select Category</option>
                      {categoryExpense.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.categoryName}
                        </option>
                      ))}
                    </select>
                    {errors?.expenses?.[index]?.category && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.expenses[index].category.message}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      {...register(`expenses.${index}.description`, {
                        required: "Description is required",
                      })}
                      placeholder="Enter description..."
                      className="w-full p-2 border rounded h-[40px]"
                    />
                    {errors?.expenses?.[index]?.description && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.expenses[index].description.message}
                      </p>
                    )}
                  </div>

                  {/* Store Name */}
                  <div className="w-[15%]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Store Name
                    </label>
                    <input
                      type="text"
                      {...register(`expenses.${index}.storeName`, {
                        required: "Store Name is required",
                      })}
                      placeholder="Enter store name..."
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  {/* Amount */}
                  <div className="w-[12%]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Amount
                    </label>
                    <input
                      type="number"
                      {...register(`expenses.${index}.amount`, {
                        required: "Amount is required",
                        min: { value: 0, message: "Amount must be positive" },
                      })}
                      placeholder="0"
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  {/* Date */}
                  <div className="w-[12%]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      {...register(`expenses.${index}.date`, {
                        required: "Date is required",
                      })}
                      className="w-full p-2 border rounded"
                      max={getTodayDate()} // ✅ Changed from min to max
                    />
                  </div>


                  {/* Remove Button */}
                  <div className="w-[5%]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Action
                    </label>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      disabled={fields.length <= 1}
                      className="text-red-500 hover:text-red-600 p-2"
                    >
                      <Trash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end items-center mt-6 pt-4 border-t gap-3">
              <button
                type="button"
                onClick={() =>
                  append({
                    employee: "",
                    category: "",
                    description: "",
                    storeName: "",
                    amount: "",
                    status: "",
                    date: getTodayDate(),
                  })
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded flex items-center gap-1"
              >
                <Plus size={16} /> Add New Expense
              </button>
              <button
                type="submit"
                disabled={
                  isLoading ||
                  (assignToMultiple === false && !selectedEmployeeId)
                }
                className="px-6 py-2 text-sm font-medium text-white bg-[#2C4E86] rounded"
              >
                {isLoading
                  ? "Submitting..."
                  : `Submit ${fields.length} Expense(s)`}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </>
  );
};
